import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import { Box, CircularProgress, Stack, Tooltip, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import ResponsiveTooltip from '../../ResponsiveTooltip';
import React from 'react';
import { usePricing } from '../../../hooks/usePricing';
import { useTranslation } from '../../../hooks/useTranslation';
import { useUserPlanStatus } from '../../../hooks/useUserPlanStatus';
import type { Package } from '../../../services/payment.types';
import { convertCentsToEuros } from '../../../services/transformers';
import { packages } from '../constants';
import { calculateEffectiveDisplayPriceEuros } from '../utils'; // Import the utility function

interface PackageSelectionProps {
    year: string;
    yearData: {
        package: Package | null;
        expertReview: boolean;
    };
    transactions: number;
    onPackageSelect: (year: string, packageName: string) => void;
    onExpertReviewToggle: (year: string) => void;
    isExpertReviewPurchased: (year: string) => boolean;
}

export const PackageSelection: React.FC<PackageSelectionProps> = ({
    year,
    yearData,
    transactions,
    onPackageSelect,
    onExpertReviewToggle,
    isExpertReviewPurchased,
}) => {
    const { userPlans, isPurchased } = useUserPlanStatus();
    const theme = useTheme();
    const { t } = useTranslation();
    const { pricing, isLoading: isPricingLoading } = usePricing();

    const formatPriceWithoutSymbol = (price: number): string => {
        // Format price without currency symbol for German format
        return price.toFixed(2).replace('.', ',');
    };

    const formatPriceGerman = (price: number): string => {
        // Format price with currency symbol on the right side (German format)
        return `${formatPriceWithoutSymbol(price)} €`;
    };

    const isPackagePurchased = (packageType: string) => {
        if (!isPurchased(year)) return false;
        const plan = userPlans?.[`taxReports.${year}`];
        return plan?.status === 'active' && plan?.package === packageType;
    };

    const getRecommendedPackage = (transactions: number) => {
        return packages.reduce(
            (prev, curr) => {
                if (transactions <= curr.transactions && (prev === null || curr.transactions < prev.transactions)) {
                    return curr;
                }
                return prev;
            },
            null as Package | null
        );
    };

    return (
        <Box sx={{ px: { xs: 2, sm: 3 } }}>
            {/* Table Header */}
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    py: 2,
                    px: 2,
                    mb: 2,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                }}
            >
                <Typography
                    variant="subtitle2"
                    sx={{
                        fontWeight: 'bold',
                        flex: '1 0 30%',
                        fontSize: { xs: '1rem', sm: '1.1rem' },
                    }}
                >
                    {t('plans.tableHeaders.package')}
                </Typography>
                <Typography
                    variant="subtitle2"
                    sx={{
                        fontWeight: 'bold',
                        width: { xs: '90px', sm: '120px' },
                        flexShrink: 0,
                        fontSize: { xs: '1rem', sm: '1.1rem' },
                    }}
                >
                    {t('plans.tableHeaders.transactionLimit')}
                </Typography>
                <Typography
                    variant="subtitle2"
                    sx={{
                        fontWeight: 'bold',
                        flex: '1 0 30%',
                        textAlign: 'right',
                        fontSize: { xs: '1rem', sm: '1.1rem' },
                    }}
                >
                    {t('plans.tableHeaders.price')}
                </Typography>
            </Box>

            {/* Table Rows */}
            <Stack spacing={1.5}>
                {packages.map((pkg) => {
                    const isCurrentlyPurchased = isPackagePurchased(pkg.id);
                    const purchasedPlanDetails = isPurchased(year) ? userPlans?.[`taxReports.${year}`] : null;
                    const purchasedPackageId = purchasedPlanDetails?.package;

                    let isDisabled = false;
                    if (purchasedPackageId) {
                        const purchasedPackageIndex = packages.findIndex((p) => p.id === purchasedPackageId);
                        const currentPackageIndex = packages.findIndex((p) => p.id === pkg.id);

                        if (purchasedPackageIndex !== -1 && currentPackageIndex !== -1) {
                            isDisabled = currentPackageIndex <= purchasedPackageIndex;
                        }
                    }

                    const recommendedPackageId = getRecommendedPackage(transactions)?.id;
                    const isSelected = yearData.package?.id === pkg.id;

                    const effectivePrice = calculateEffectiveDisplayPriceEuros(pkg, year, userPlans, pricing);

                    const packageData = pricing?.packages
                        ? pricing.packages[pkg.id as keyof typeof pricing.packages]
                        : null;
                    const fullPriceInEuros = packageData?.price ? convertCentsToEuros(packageData.price) : pkg.price;

                    const hasDiscount = effectivePrice < fullPriceInEuros && !isCurrentlyPurchased && !isDisabled;

                    const displayPrice = formatPriceGerman(effectivePrice);

                    const dynamicTransactions = packageData?.transactions || pkg.transactions;

                    return (
                        <ResponsiveTooltip 
                            key={pkg.id}
                            title={isCurrentlyPurchased ? t('plans.alreadyPurchased') : ''}
                            arrow
                            placement="top"
                        >
                            <Box
                                onClick={() => !isDisabled && onPackageSelect(year, pkg.id)}
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    alignItems: 'center',
                                    p: { xs: 2, sm: 2.5 },
                                    cursor: isDisabled ? 'default' : 'pointer',
                                    borderRadius: 2,
                                    border: '2px solid',
                                    borderColor: isSelected ? 'primary.main' : 'transparent',
                                    backgroundColor: 'background.paper',
                                    opacity: isDisabled && !isCurrentlyPurchased ? 0.6 : 1,
                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                    boxShadow: isSelected
                                        ? '0 4px 20px rgba(25, 118, 210, 0.15)'
                                        : '0 1px 3px rgba(0,0,0,0.05)',
                                    '&:hover': {
                                        backgroundColor: isDisabled ? 'background.paper' : 'action.hover',
                                        transform: isDisabled ? 'none' : 'translateY(-1px)',
                                        boxShadow: isSelected
                                            ? '0 8px 30px rgba(25, 118, 210, 0.25)'
                                            : '0 4px 12px rgba(0,0,0,0.1)',
                                    },
                                    ...(isCurrentlyPurchased && {
                                        '& .MuiTypography-root': { color: '#757575 !important' },
                                    }),
                                }}
                            >
                                {/* Package Name */}
                                <Typography
                                    variant="body1"
                                    fontWeight="bold"
                                    sx={{
                                        flex: '1 0 30%',
                                        fontSize: { xs: '1rem', sm: '1.1rem' },
                                    }}
                                >
                                    {t(`plansData.packages.${pkg.id}.name`)}
                                </Typography>

                                {/* Transactions */}
                                <Typography
                                    variant="body2"
                                    sx={{
                                        width: { xs: '90px', sm: '120px' },
                                        flexShrink: 0,
                                        fontSize: { xs: '1rem', sm: '1.1rem' },
                                        color: 'text.secondary',
                                    }}
                                >
                                    {dynamicTransactions}
                                </Typography>

                                {/* Price */}
                                <Box
                                    sx={{
                                        flex: '1 0 30%',
                                        display: 'flex',
                                        justifyContent: 'flex-end',
                                        alignItems: 'center',
                                    }}
                                >
                                    {/* Recommendation Badge */}
                                    {recommendedPackageId === pkg.id && !isPurchased(year) && (
                                        <ResponsiveTooltip title="Unsere Empfehlung" arrow placement="top">
                                            <Box
                                                sx={{
                                                    backgroundColor: theme.palette.primary.main,
                                                    color: theme.palette.primary.contrastText,
                                                    p: 0.5,
                                                    borderRadius: '50%',
                                                    mr: 1.5,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    width: 28,
                                                    height: 28,
                                                }}
                                            >
                                                <ThumbUpIcon sx={{ fontSize: '1rem' }} />
                                            </Box>
                                        </ResponsiveTooltip>
                                    )}

                                    {hasDiscount ? (
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'row',
                                                alignItems: 'center',
                                                gap: 1,
                                            }}
                                        >
                                            <Typography
                                                component="span"
                                                sx={{
                                                    color: '#757575',
                                                    fontSize: { xs: '0.95rem', sm: '1rem' },
                                                }}
                                            >
                                                ({formatPriceWithoutSymbol(fullPriceInEuros)} € -
                                                {formatPriceWithoutSymbol(fullPriceInEuros - effectivePrice)} €)
                                            </Typography>

                                            <Typography
                                                variant="body1"
                                                fontWeight="bold"
                                                sx={{
                                                    fontSize: { xs: '1rem', sm: '1.1rem' },
                                                    color: isCurrentlyPurchased
                                                        ? '#757575'
                                                        : theme.palette.text.primary,
                                                }}
                                            >
                                                {isCurrentlyPurchased ? `(${t('plans.alreadyPurchased')}) ` : ''}
                                                {displayPrice}
                                            </Typography>
                                        </Box>
                                    ) : (
                                        <Typography
                                            variant="body1"
                                            fontWeight="bold"
                                            sx={{
                                                fontSize: { xs: '1rem', sm: '1.1rem' },
                                                color: theme.palette.text.primary,
                                            }}
                                        >
                                            {isCurrentlyPurchased ? `(${t('plans.alreadyPurchased')}) ` : ''}
                                            {displayPrice}
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                        </ResponsiveTooltip>
                    );
                })}
            </Stack>

            {/* Expert Review Section - Banner Format */}
            <Box sx={{ mt: 4, mb: 3 }}>
                <ResponsiveTooltip title={isExpertReviewPurchased(year) ? t('plans.alreadyPurchased') : ''} arrow placement="top">
                    <Box
                        onClick={() => {
                            if (!isExpertReviewPurchased(year)) {
                                onExpertReviewToggle(year);
                            }
                        }}
                        sx={{
                            width: '100%',
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            p: { xs: 2, sm: 2.5 },
                            position: 'relative',
                            cursor: isExpertReviewPurchased(year) ? 'default' : 'pointer',
                            borderRadius: 2,
                            border: '2px solid',
                            borderColor: yearData.expertReview ? 'primary.main' : 'divider',
                            backgroundColor: 'background.paper',
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            boxShadow: yearData.expertReview
                                ? '0 4px 20px rgba(25, 118, 210, 0.15)'
                                : '0 1px 3px rgba(0,0,0,0.05)',
                            minHeight: '64px',
                            opacity: 1,
                            '&:hover': {
                                backgroundColor: isExpertReviewPurchased(year) ? 'background.paper' : 'action.hover',
                                transform: isExpertReviewPurchased(year) ? 'none' : 'translateY(-1px)',
                                boxShadow: yearData.expertReview
                                    ? '0 8px 30px rgba(25, 118, 210, 0.25)'
                                    : '0 4px 12px rgba(0,0,0,0.1)',
                            },
                            ...(isExpertReviewPurchased(year) && {
                                '& .MuiTypography-root': { color: '#757575 !important' },
                            }),
                        }}
                    >
                        {/* Expert Review Name and Description */}
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                flex: '1 1 auto',
                            }}
                        >
                            <Typography
                                variant="body1"
                                fontWeight="bold"
                                sx={{
                                    fontSize: { xs: '1rem', sm: '1.1rem' },
                                }}
                            >
                                <span style={{ fontWeight: 'bold' }}>Experten-Check (optional): </span>
                                <Box component="span" sx={{ display: { xs: 'none', md: 'inline' }, fontWeight: 400 }}>
                                    {t('plans.expertReview.description')}
                                </Box>
                                <ResponsiveTooltip
                                    title={
                                        <Box
                                            sx={{ width: { xs: 260, md: 500 }, p: 1.5, maxWidth: { xs: 260, md: 500 } }}
                                        >
                                            {/* Show description at top in tooltip only for mobile */}
                                            <Typography
                                                variant="body2"
                                                sx={{ fontSize: '0.9rem', display: { xs: 'block', md: 'none' }, mb: 1 }}
                                            >
                                                {t('plans.expertReview.description')}
                                            </Typography>
                                            <Typography variant="body2" sx={{ fontSize: '0.9rem' }}>
                                                {t('plans.expertReview.tooltip.line1')}
                                                <br />
                                                <br />
                                                {t('plans.expertReview.tooltip.line2')}
                                                <br />
                                                <br />
                                                {t('plans.expertReview.tooltip.line3')}
                                            </Typography>
                                        </Box>
                                    }
                                    arrow
                                    placement="bottom"
                                    componentsProps={{
                                        tooltip: {
                                            sx: {
                                                bgcolor: (theme) => alpha(theme.palette.background.paper, 0.9),
                                                backdropFilter: 'blur(5px)',
                                                color: theme.palette.text.primary,
                                                boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
                                                border: '1px solid',
                                                borderColor: theme.palette.divider,
                                                borderRadius: 1,
                                                p: 0,
                                                maxWidth: 'none',
                                                fontSize: '0.9rem',
                                                '& .MuiTooltip-arrow': {
                                                    color: (theme) => alpha(theme.palette.background.paper, 0.9),
                                                },
                                            },
                                        },
                                        popper: {
                                            sx: {
                                                '& .MuiTooltip-tooltip': {
                                                    p: 0,
                                                },
                                            },
                                        },
                                    }}
                                >
                                    <InfoOutlinedIcon
                                        sx={{
                                            fontSize: '1rem',
                                            ml: 0.5,
                                            color: 'text.secondary',
                                            cursor: 'help',
                                            verticalAlign: 'middle',
                                        }}
                                    />
                                </ResponsiveTooltip>
                            </Typography>
                        </Box>

                        {/* Price */}
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                                alignItems: 'center',
                                ml: 2,
                            }}
                        >
                            <Typography
                                variant="body1"
                                fontWeight="bold"
                                sx={{
                                    fontSize: { xs: '1rem', sm: '1.1rem' },
                                    color: theme.palette.text.primary,
                                    textAlign: 'right',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {isExpertReviewPurchased(year) ? `(${t('plans.alreadyPurchased')}) ` : ''}
                                {isPricingLoading ? (
                                    <CircularProgress size={16} />
                                ) : pricing?.addons?.expertReview?.price ? (
                                    formatPriceGerman(convertCentsToEuros(pricing.addons.expertReview.price))
                                ) : (
                                    '39,99 €'
                                )}
                            </Typography>
                        </Box>
                    </Box>
                </ResponsiveTooltip>
            </Box>
        </Box>
    );
};
